import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  InputAdornment,
  Popover,
} from '@mui/material';
import { LocationOn as LocationIcon, Map as MapIcon } from '@mui/icons-material';
import { ftthCaixaService, FtthCaixa } from '../../services/ftthCaixaService';
import { MapSelectorModal } from '../common';
import EscritorioSelect from '../selects/EscritorioSelect';
import { CAIXA_DEFAULT_ICON_ID } from '../../utils/mapPinIcons';

export interface FtthCaixaCeoFormProps {
  onSuccess?: (caixa: FtthCaixa) => void;
  onCancel?: () => void;
  caixa?: FtthCaixa;
  coordinates?: { lat: number; lng: number };
  isFullscreen?: boolean;
}

interface FtthCaixaCeoFormData {
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  status: 'ativo' | 'inativo' | 'aguardando_instalacao' | 'manutencao';
  tipo_rede: 'propria' | 'neutra';
  operadora: string;
  color: string;
  color_text: string;
  escritorio_id: number;
}

const COLOR_PALETTE = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
  '#FFD700', '#FFA500', '#FF6347', '#DC143C', '#8B0000', '#006400', '#228B22', '#4169E1',
  '#4B0082', '#800080', '#808080', '#A9A9A9', '#D3D3D3', '#FFE4B5', '#F0E68C', '#98FB98',
  '#87CEEB', '#DDA0DD', '#F5DEB3', '#DEB887', '#BC8F8F', '#2F4F4F', '#696969', '#C0C0C0',
];

const FtthCaixaCeoForm: React.FC<FtthCaixaCeoFormProps> = ({
  onSuccess,
  onCancel,
  caixa,
  coordinates,
  isFullscreen,
}) => {
  const [formData, setFormData] = useState<FtthCaixaCeoFormData>({
    name: caixa?.name || '',
    address: caixa?.address || '',
    latitude: coordinates?.lat ? coordinates.lat.toString() : caixa?.latitude ? caixa.latitude.toString() : '',
    longitude: coordinates?.lng ? coordinates.lng.toString() : caixa?.longitude ? caixa.longitude.toString() : '',
    status: caixa?.status || 'ativo',
    tipo_rede: (caixa as any)?.tipo_rede || 'propria',
    operadora: (caixa as any)?.operadora || '',
    color: (caixa as any)?.color || '#FF0000',
    color_text: (caixa as any)?.color_text || '#FFFFFF',
    escritorio_id: (caixa as any)?.escritorio_id || 1,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<{ el: HTMLElement } | null>(null);

  useEffect(() => {
    if (caixa) {
      setFormData({
        name: caixa.name || '',
        address: caixa.address || '',
        latitude: coordinates?.lat ? coordinates.lat.toString() : caixa.latitude ? caixa.latitude.toString() : '',
        longitude: coordinates?.lng ? coordinates.lng.toString() : caixa.longitude ? caixa.longitude.toString() : '',
        status: caixa.status || 'ativo',
        tipo_rede: (caixa as any)?.tipo_rede || 'propria',
        operadora: (caixa as any)?.operadora || '',
        color: (caixa as any)?.color || '#FF0000',
        color_text: (caixa as any)?.color_text || '#FFFFFF',
        escritorio_id: (caixa as any)?.escritorio_id || 1,
      });
    }
  }, [caixa, coordinates]);

  const handleChange = (field: keyof FtthCaixaCeoFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field: keyof FtthCaixaCeoFormData) => (event: any) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleOpenMapModal = () => {
    if (formData.latitude && formData.longitude) {
      setSelectedLocation({
        lat: parseFloat(formData.latitude),
        lng: parseFloat(formData.longitude),
      });
    } else {
      setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
    }
    setMapModalOpen(true);
  };

  const handleConfirmMapLocation = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString(),
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.address.trim()) {
      setError('Endereço é obrigatório');
      return false;
    }
    if (!formData.escritorio_id) {
      setError('Escritório é obrigatório');
      return false;
    }
    if (formData.tipo_rede === 'neutra' && !formData.operadora.trim()) {
      setError('Operadora é obrigatória quando o tipo de rede é neutra');
      return false;
    }
    // Validar coordenadas (obrigatórias)
    if (!formData.latitude || !formData.longitude) {
      setError('Latitude e longitude são obrigatórias');
      return false;
    }

    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      setError('Latitude deve estar entre -90 e 90');
      return false;
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      setError('Longitude deve estar entre -180 e 180');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!validateForm()) return;

    try {
      setLoading(true);
      const submitData = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        status: formData.status,
        tipo: 'ceo' as const,
        tipo_rede: formData.tipo_rede,
        operadora: formData.tipo_rede === 'neutra' ? formData.operadora.trim() : undefined,
        color: formData.color.trim() || undefined,
        color_text: formData.color_text.trim() || undefined,
        icon: CAIXA_DEFAULT_ICON_ID,
        escritorio_id: formData.escritorio_id,
      };

      const response = caixa
        ? await ftthCaixaService.update(caixa.id, submitData)
        : await ftthCaixaService.create(submitData);

      const isSuccess =
        response &&
        (response.success === true || response.data || ('id' in response && response.id));
      if (isSuccess && onSuccess) {
        const caixaData = response.data || response;
        onSuccess(caixaData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar CEO');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {caixa ? 'Editar CEO' : 'Nova CEO'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary">
            Informações Básicas
          </Typography>
        </Grid>

        <Grid item xs={12} sm={12}>
          <TextField
            fullWidth
            label="Nome da CEO"
            value={formData.name}
            onChange={handleChange('name')}
            required
            size="small"
            placeholder="Ex: CEO-01 Bairro Norte"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" sx={{ '& .MuiPopover-root': { zIndex: isFullscreen ? 10004 : 1500 } }}>
            <InputLabel>Tipo de rede</InputLabel>
            <Select
              value={formData.tipo_rede}
              onChange={(e) => {
                const value = e.target.value as 'propria' | 'neutra';
                setFormData((prev) => ({ ...prev, tipo_rede: value, ...(value === 'propria' ? { operadora: '' } : {}) }));
              }}
              label="Tipo de rede"
              MenuProps={{ sx: { zIndex: isFullscreen ? 10004 : 1500 } }}
            >
              <MenuItem value="propria">Própria</MenuItem>
              <MenuItem value="neutra">Neutra</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Operadora"
            value={formData.operadora}
            onChange={handleChange('operadora')}
            size="small"
            placeholder="Nome da operadora"
            required={formData.tipo_rede === 'neutra'}
            disabled={formData.tipo_rede === 'propria'}
          />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
          <Typography variant="subtitle2" color="primary">
            Localização
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={handleOpenMapModal}
          >
            Localização
          </Button>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Latitude"
            value={formData.latitude}
            onChange={handleChange('latitude')}
            size="small"
            type="number"
            required
            placeholder="-23.550520"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon fontSize="small" />
                </InputAdornment>
              ),
              inputProps: { step: 'any', min: -90, max: 90 },
            }}
            helperText="Latitude obrigatória"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Longitude"
            value={formData.longitude}
            onChange={handleChange('longitude')}
            size="small"
            type="number"
            required
            placeholder="-46.633309"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon fontSize="small" />
                </InputAdornment>
              ),
              inputProps: { step: 'any', min: -180, max: 180 },
            }}
            helperText="Longitude obrigatória"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Endereço"
            value={formData.address}
            onChange={handleChange('address')}
            required
            size="small"
            placeholder="Rua, número, bairro, cidade"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
            Personalização do ícone no mapa
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Cor do ícone
                </Typography>
                <Box
                  onClick={(e) => setColorPickerAnchor({ el: e.currentTarget })}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: formData.color || '#ccc',
                      border: '1px solid #999',
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formData.color || '#FF0000'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Popover
          open={!!colorPickerAnchor}
          anchorEl={colorPickerAnchor?.el}
          onClose={() => setColorPickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { zIndex: isFullscreen ? 10004 : 1500 } } }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Cor do ícone
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: 208 }}>
              {COLOR_PALETTE.map((hex) => (
                <Box
                  key={hex}
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, color: hex }));
                    setColorPickerAnchor(null);
                  }}
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: hex,
                    border: '1px solid',
                    borderColor: hex === '#FFFFFF' ? '#ccc' : 'transparent',
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    '&:hover': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                  title={hex}
                />
              ))}
            </Box>
          </Box>
        </Popover>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 0, mt: 2 }}>
            Status e Escritório
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <EscritorioSelect
            value={formData.escritorio_id}
            onChange={(value) => setFormData((prev) => ({ ...prev, escritorio_id: Number(value) || 1 }))}
            fullWidth
            size="small"
            required
            label="Escritório"
            placeholder="Selecione um escritório"
            onlyActive={true}
            allowClear={false}
            multiple={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required sx={{ '& .MuiPopover-root': { zIndex: isFullscreen ? 10004 : 1500 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={handleSelectChange('status')}
              label="Status"
              MenuProps={{ sx: { zIndex: isFullscreen ? 10004 : 1500 } }}
            >
              <MenuItem value="ativo">Ativo</MenuItem>
              <MenuItem value="inativo">Inativo</MenuItem>
              <MenuItem value="aguardando_instalacao">Aguardando Instalação</MenuItem>
              <MenuItem value="manutencao">Manutenção</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Salvando...' : caixa ? 'Atualizar' : 'Criar CEO'}
        </Button>
      </Box>

      <MapSelectorModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        onConfirm={handleConfirmMapLocation}
        initialCoordinates={selectedLocation}
        title="📍 Selecionar Localização no Mapa"
        description="Clique no mapa para selecionar a localização da CEO"
      />
    </Box>
  );
};

export default FtthCaixaCeoForm;
